import { useMemo, useSyncExternalStore } from 'react';
import { format } from 'date-fns';
import { getPendingPurchases } from '../api';
import { getToken } from '../api/auth';
import useCurrentUser from './useCurrentUser';
import usePurchaseEvents from './usePurchaseEvents';
import { getSelectedCompanyId } from '../utils/selectedCompany';
import { normalizePaginatedResponse } from '../utils/pagination';
import { filterVisiblePendingPurchases } from '../utils/pendingPurchases';
import { getPendingPurchasesResource } from '../utils/pendingPurchasesResource';

const REFRESH_EVENTS = new Set([
  'purchase-created', 'purchase-updated', 'purchase-deleted', 'purchase-paid',
  'purchase-suspended', 'purchase-resumed', 'purchase-reopened', 'purchases.resync',
]);

const usePendingPurchases = (branchId, options = {}) => {
  const { enableRealtime = false, includeSuspended = true, params = {} } = options;
  const { user } = useCurrentUser();
  const companyId = getSelectedCompanyId();
  const token = getToken();
  const paramsKey = JSON.stringify(Object.fromEntries(Object.entries(params || {}).sort(([a], [b]) => a.localeCompare(b))));
  // The token is used solely as in-memory identity, never logged or persisted.
  const scope = JSON.stringify([token, companyId, user?.id, user?.authVersion,
    user?.permissions, branchId, includeSuspended, paramsKey]);
  const resource = useMemo(() => getPendingPurchasesResource(scope, async () => {
    if (!branchId) return [];
    const today = format(new Date(), 'yyyy-MM-dd');
    const result = await getPendingPurchases(branchId, today, today, undefined,
      includeSuspended, { pageSize: 0, ...JSON.parse(paramsKey) });
    return filterVisiblePendingPurchases(normalizePaginatedResponse(result).data);
  }, () => getToken() === token && getSelectedCompanyId() === companyId),
  [scope, branchId, companyId, token, paramsKey, includeSuspended]);
  const state = useSyncExternalStore(resource.subscribe, resource.getSnapshot, resource.getSnapshot);

  usePurchaseEvents(branchId, (event) => {
    if (enableRealtime && REFRESH_EVENTS.has(event?.type)
      && (!event.branchId || String(event.branchId) === String(branchId))
      && (!event.companyId || String(event.companyId) === companyId)) {
      resource.refresh();
    }
  }, { enabled: !!branchId && enableRealtime });

  return { ...state, reload: resource.refresh };
};

export default usePendingPurchases;
